import { LISTING_PLACEHOLDER_IMAGE } from "../../constants/automapListingConstants";
import { formatMoney as formatMoneyDefault } from "../../intl/formatters.js";
import { formatMileageKm, formatVehicleTitle } from "../automapListing/listingFormatters";
import { buildListingDetailHref } from "../automapListing/listingViewModels";

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return null;
}

function resolveImageUrl(listing) {
  const candidate = firstNonEmpty(
    listing?.primaryImageUrl,
    listing?.imageUrl,
    Array.isArray(listing?.images) ? listing.images[0]?.url || listing.images[0] : null
  );
  if (!candidate || candidate === LISTING_PLACEHOLDER_IMAGE) {
    return null;
  }
  return candidate;
}

/**
 * Normalize listing fields for the AutoMapa WhatsApp widget (M2+).
 * Keeps a stable shape so M3 intake can reuse the same context.
 */
export function buildAutomapWhatsappVehicleContext({
  listing = null,
  slug = "automap",
  locale = null,
  currency = "COP",
  formatMoney = null,
  dealer = null,
} = {}) {
  if (!listing || typeof listing !== "object") {
    return null;
  }

  const listingId = firstNonEmpty(listing.vehicleListingId, listing.id, listing.listingId);
  const make = firstNonEmpty(listing.make);
  const model = firstNonEmpty(listing.model);
  const year = listing.year != null && listing.year !== "" ? Number(listing.year) || listing.year : null;
  const trim = firstNonEmpty(listing.trim, listing.version);
  const priceCop =
    listing.priceCop != null && Number.isFinite(Number(listing.priceCop))
      ? Number(listing.priceCop)
      : null;
  const displayCurrency = firstNonEmpty(currency, listing.currency, "COP") || "COP";
  const moneyFormatter = typeof formatMoney === "function" ? formatMoney : formatMoneyDefault;
  const priceLabel =
    priceCop != null
      ? moneyFormatter(priceCop, { displayCurrency, locale: locale || undefined })
      : null;
  const mileageKm =
    listing.mileageKm != null && Number.isFinite(Number(listing.mileageKm))
      ? Number(listing.mileageKm)
      : null;
  const city = firstNonEmpty(listing.city, listing.dealerCity, listing.location?.city);
  const dealerId = firstNonEmpty(
    dealer?.id,
    dealer?.dealerId,
    listing.dealerId,
    listing.dealer?.id
  );
  const dealerName = firstNonEmpty(
    dealer?.name,
    dealer?.dealerName,
    listing.dealerName,
    listing.dealer?.name,
    listing.dealer?.dealerName
  );

  const listingPath = buildListingDetailHref(slug, listing, locale);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const listingUrl = listingPath ? `${origin}${listingPath}` : null;
  const title = formatVehicleTitle(listing);

  return {
    listingId,
    make,
    model,
    year,
    trim,
    priceCop,
    currency: displayCurrency,
    priceLabel,
    mileageKm,
    mileageLabel: mileageKm != null ? formatMileageKm(mileageKm) : null,
    city,
    dealerId,
    dealerName,
    listingPath,
    listingUrl,
    imageUrl: resolveImageUrl(listing),
    title,
  };
}

export function hasAutomapWhatsappVehicleContext(vehicleContext) {
  return Boolean(vehicleContext?.listingId || vehicleContext?.title || vehicleContext?.make);
}
